import { BaseButton } from '@/components/common/base-button';
import BackspaceIcon from '@/components/icons/parking/Backspace';
import { useTranslation } from 'react-i18next';
import { useAccessibilityTTS } from '@/hooks';
import { useConfigStore } from '@/stores';
import clsx from 'clsx';
import { useCallback } from 'react';
import styles from './styles.module.scss';
interface NumberKeypadProps {
    onNumberClick: (num: string) => void;
    onClearAll: () => void;
    onBackspace: () => void;
    currentInput?: string;
}

export default function NumberKeypad({ onNumberClick, onClearAll, onBackspace, currentInput = '' }: NumberKeypadProps) {
    const baseClass = clsx(styles['number-keypad'], 'flex h-[calc(72*var(--vh-unit))] w-[calc(72*var(--vh-unit))] items-center justify-center rounded-full');
    const numberClass = `${baseClass} bg-[color:var(--color-bg-number-pad)] border border-[var(--color-primary-white)] text-center text-[calc(36*var(--vh-unit))] font-normal leading-[calc(36*var(--vh-unit))] tracking-[calc(-0.792*var(--vh-unit))] text-[color:var(--color-text-sub01)] flex-shrink-0`;
    const actionBaseClass = clsx(
        styles['action-button'],
        'flex h-[calc(72*var(--vh-unit))] w-[calc(72*var(--vh-unit))] items-center justify-center rounded-full'
    );
    const actionClass = `${actionBaseClass} bg-[color:var(--color-text-sub01)] text-[color:var(--color-text-white)] text-center text-[calc(16*var(--vh-unit))] font-medium leading-[calc(18*var(--vh-unit))]`;
    const { t } = useTranslation();

    // Helper function to add/remove active class directly to DOM element
    const addActiveClass = useCallback((event: React.MouseEvent<HTMLButtonElement>, isActionButton = false) => {
        const target = event.currentTarget;
        const activeClass = isActionButton ? styles['action-button__active'] : styles['number-keypad__active'];
        target.classList.add(activeClass);

        const timeout = setTimeout(() => {
            target.classList.remove(activeClass);
        }, 200);

        return () => clearTimeout(timeout);
    }, []);

    // Custom TTS handling
    const { speak } = useAccessibilityTTS();
    const voice = useConfigStore((state) => state?.config?.tts?.voice);

    // Click TTS function
    const speakNumberClick = (num: string) => {
        if (!voice) return;
        const newInput = currentInput.length < 4 ? currentInput + num : currentInput;
        const displayArray = [];
        for (let i = 0; i < newInput.length && i < 4; i++) {
            displayArray[i] = newInput[i];
        }
        const newDisplayValue = displayArray.join(' ');
        speak(`${num} 입력 ${newDisplayValue}`);
    };

    // Focus TTS functions
    const speakNumberFocus = (num: string) => {
        if (!voice) return;
        speak(`${num}, ${t('common.button.tts.button')}`);
    };

    const speakClearAll = () => {
        onClearAll();
    };

    const speakBackspace = () => {
        if (!voice) {
            onBackspace();
            return;
        }

        const deletedNumber = currentInput.length > 0 ? currentInput[currentInput.length - 1] : '';

        const newInput = currentInput.slice(0, -1);

        const displayArray = [];
        for (let i = 0; i < newInput.length && i < 4; i++) {
            displayArray[i] = newInput[i];
        }
        const newDisplayValue = displayArray.join(' ');

        if (deletedNumber) {
            speak(`${deletedNumber} 삭제됨 ${newDisplayValue}`);
        } else {
            return;
        }

        onBackspace();
    };

    const renderNumberButton = (num: string, tabIndex: number) => (
        <BaseButton
            tabIndex={tabIndex}
            data-interactive
            data-tts-scope="number-keypad"
            aria-label={num}
            role="button"
            className={numberClass}
            onClick={(event) => {
                addActiveClass(event);
                speakNumberClick(num);
                onNumberClick(num);
            }}
            onFocus={() => speakNumberFocus(num)}
        >
            <span className="number">{num}</span>
        </BaseButton>
    );

    return (
        <div className="flex flex-col items-start gap-[calc(8*var(--vh-unit))]">
            <div className="flex flex-col items-start gap-[calc(8*var(--vh-unit))]" data-region-nav data-region-order={5}>
                <div className="flex items-start gap-[calc(28*var(--vh-unit))]">
                    {renderNumberButton('1', 5)}
                    {renderNumberButton('2', 5)}
                    {renderNumberButton('3', 5)}
                </div>
                <div className="flex items-start gap-[calc(28*var(--vh-unit))]">
                    {renderNumberButton('4', 5)}
                    {renderNumberButton('5', 5)}
                    {renderNumberButton('6', 5)}
                </div>
                <div className="flex items-start gap-[calc(28*var(--vh-unit))]">
                    {renderNumberButton('7', 5)}
                    {renderNumberButton('8', 5)}
                    {renderNumberButton('9', 5)}
                </div>
            </div>
            <div className="flex items-start gap-[calc(28*var(--vh-unit))]">
                <BaseButton
                    aria-label={t('parking.resetButton.ariaLabel')}
                    data-region-nav
                    data-region-order={6}
                    tabIndex={6}
                    role="button"
                    type="button"
                    className={actionClass}
                    onClick={(event) => {
                        addActiveClass(event, true);
                        speakClearAll();
                    }}
                >
                    <span className="text-center text-[calc(14*var(--vh-unit))] leading-[calc(17.5*var(--vh-unit))]">
                        {t('parking.resetButton.textBefore')}
                        <br />
                        {t('parking.resetButton.textAfter')}
                    </span>
                </BaseButton>
                <BaseButton
                    data-region-order={5}
                    data-interactive
                    data-tts-scope="number-keypad"
                    role="button"
                    type="button"
                    aria-label={'0'}
                    tabIndex={5}
                    className={numberClass}
                    onClick={(event) => {
                        addActiveClass(event);
                        speakNumberClick('0');
                        onNumberClick('0');
                    }}
                    onFocus={() => speakNumberFocus('0')}
                >
                    <span className="number">0</span>
                </BaseButton>
                <BaseButton
                    aria-label={t('parking.backspaceButton.ariaLabel')}
                    data-region-nav
                    data-region-order={7}
                    tabIndex={7}
                    role="button"
                    type="button"
                    className={actionClass}
                    onClick={(event) => {
                        addActiveClass(event, true);
                        speakBackspace();
                    }}
                >
                    <BackspaceIcon style={{ width: 'calc(48*var(--vw-unit))', height: 'calc(48*var(--vh-unit))' }} />
                </BaseButton>
            </div>
        </div>
    );
}
